import { toast as originalToast } from 'react-hot-toast'

interface ToastGroup {
  key: string
  count: number
  lastNotification: number
  messages: Set<string>
}

class ToastManager {
  private groups = new Map<string, ToastGroup>()
  private readonly MAX_TOASTS_PER_GROUP = 2
  private readonly THROTTLE_DURATION = 5000 // 5 seconds
  private readonly SIMILAR_MESSAGE_THRESHOLD = 1000 // 1 second for similar messages

  /**
   * Throttled toast notification that limits spam
   * @param type - Toast type (success, error, info)
   * @param message - Message to display
   * @param groupKey - Group key for related notifications (e.g., 'login', 'connection', 'file-transfer')
   * @param options - Toast options
   */
  private throttledToast(
    type: 'success' | 'error' | 'info',
    message: string,
    groupKey: string,
    options?: any
  ) {
    const now = Date.now()
    const group = this.groups.get(groupKey)

    // Check if this exact message was sent recently (prevent duplicates)
    if (group?.messages.has(message) && now - group.lastNotification < this.SIMILAR_MESSAGE_THRESHOLD) {
      return
    }

    if (!group) {
      // First notification in this group
      this.groups.set(groupKey, {
        key: groupKey,
        count: 1,
        lastNotification: now,
        messages: new Set([message])
      })
      if (type === 'info') {
        originalToast(message, options)
      } else {
        originalToast[type](message, options)
      }
      return
    }

    // Reset count if enough time has passed
    if (now - group.lastNotification > this.THROTTLE_DURATION) {
      group.count = 0
      group.messages.clear()
    }

    // Allow notification if under limit
    if (group.count < this.MAX_TOASTS_PER_GROUP) {
      group.count++
      group.lastNotification = now
      group.messages.add(message)
      if (type === 'info') {
        originalToast(message, options)
      } else {
        originalToast[type](message, options)
      }
    }
    // Otherwise, silently ignore (console logs will still work)
  }

  /**
   * Success toast with throttling
   */
  success(message: string, groupKey: string = 'general', options?: any) {
    this.throttledToast('success', message, groupKey, options)
  }

  /**
   * Error toast with throttling
   */
  error(message: string, groupKey: string = 'general', options?: any) {
    this.throttledToast('error', message, groupKey, options)
  }

  /**
   * Info toast with throttling
   */
  info(message: string, groupKey: string = 'general', options?: any) {
    this.throttledToast('info', message, groupKey, options)
  }

  /**
   * Clear throttling for a specific group
   */
  clearGroup(groupKey: string) {
    this.groups.delete(groupKey)
  }

  /**
   * Clear all throttling
   */
  clearAll() {
    this.groups.clear()
  }

  /**
   * Get current group status (for debugging)
   */
  getGroupStatus(groupKey: string) {
    return this.groups.get(groupKey)
  }

  /**
   * Force a toast (bypass throttling) - use sparingly for critical notifications
   */
  force = {
    success: (message: string, options?: any) => originalToast.success(message, options),
    error: (message: string, options?: any) => originalToast.error(message, options),
    info: (message: string, options?: any) => originalToast(message, options),
  }
}

// Create singleton instance
export const toastManager = new ToastManager()

// Export convenient toast functions with throttling
export const toast = {
  success: (message: string, groupKey: string = 'general', options?: any) =>
    toastManager.success(message, groupKey, options),
  error: (message: string, groupKey: string = 'general', options?: any) =>
    toastManager.error(message, groupKey, options),
  info: (message: string, groupKey: string = 'general', options?: any) =>
    toastManager.info(message, groupKey, options),
  // For backwards compatibility with existing code
  warn: (message: string, groupKey: string = 'general', options?: any) =>
    toastManager.info(message, groupKey, options),
}

// Group key constants for consistency
export const TOAST_GROUPS = {
  LOGIN: 'login',
  CONNECTION: 'connection',
  FILE_TRANSFER: 'file-transfer',
  CALL: 'call',
  AVATAR: 'avatar',
  NETWORK: 'network',
  GENERAL: 'general',
} as const

export type ToastGroupKey = typeof TOAST_GROUPS[keyof typeof TOAST_GROUPS]